import React, { useCallback, useLayoutEffect, useMemo, useRef, useState, useEffect } from 'react';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';

function clampIndex(i: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, i));
}

export interface DynamicsChartZoomShellProps<T> {
  /** Full time-series rows (same reference identity can change when data reloads). */
  data: T[];
  /** Must match `ResponsiveContainer` height in the child chart (default 320). */
  chartHeight?: number;
  /** Renders the Recharts chart for the visible window (use ResponsiveContainer width="100%"). */
  children: (visibleData: T[]) => React.ReactNode;
}

const DEFAULT_CHART_HEIGHT = 320;

/**
 * X-axis zoom + horizontal pan (Dynamics A pattern). Zooming in also scales the chart slightly
 * on Y so detail is easier to read; extra height scrolls with the card / fullscreen panel.
 */
export function DynamicsChartZoomShell<T>({
  data,
  chartHeight: chartHeightProp,
  children,
}: DynamicsChartZoomShellProps<T>) {
  const chartHeight =
    typeof chartHeightProp === 'number' && Number.isFinite(chartHeightProp) && chartHeightProp > 0
      ? chartHeightProp
      : DEFAULT_CHART_HEIGHT;
  const plotHeight = useDynamicsPlotHeight(chartHeight);
  const [brushManual, setBrushManual] = useState<{ start: number; end: number } | null>(null);

  useEffect(() => {
    setBrushManual(null);
  }, [data.length]);

  const brush = useMemo(() => {
    const n = data.length;
    if (!n) return { start: 0, end: 0 };
    const full = { start: 0, end: n - 1 };
    if (!brushManual) return full;
    const s = clampIndex(brushManual.start, 0, n - 1);
    const e = clampIndex(brushManual.end, s, n - 1);
    return { start: s, end: e };
  }, [data.length, brushManual]);

  const visibleData = useMemo(
    () => (data.length ? data.slice(brush.start, brush.end + 1) : []),
    [data, brush]
  );

  const viewportRef = useRef<HTMLDivElement>(null);
  const [hostWidth, setHostWidth] = useState(0);
  const dataLenRef = useRef(data.length);
  dataLenRef.current = data.length;
  const brushRef = useRef(brush);
  brushRef.current = brush;
  const ignoreScrollSyncRef = useRef(false);

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && Number.isFinite(w)) {
        setHostWidth(Math.max(0, Math.floor(w)));
      }
    });
    ro.observe(el);
    setHostWidth(Math.max(0, Math.floor(el.getBoundingClientRect().width)));
    return () => ro.disconnect();
  }, []);

  const innerTrackWidthPx = useMemo(() => {
    const n = data.length;
    if (!n) return Math.max(hostWidth || 560, 320);
    const spanVisible = brush.end - brush.start + 1;
    const base = Math.max(hostWidth || 320, 280);
    if (spanVisible >= n) return base;
    return Math.max(base, Math.ceil((base * n) / Math.max(spanVisible, 1)));
  }, [data.length, hostWidth, brush.start, brush.end]);

  useLayoutEffect(() => {
    const host = viewportRef.current;
    if (!host) return;
    const n = data.length;
    if (n <= 1) return;
    const b = brush;
    const w = b.end - b.start + 1;

    const apply = () => {
      ignoreScrollSyncRef.current = true;
      try {
        if (w >= n) {
          host.scrollLeft = 0;
          return;
        }
        const maxScroll = host.scrollWidth - host.clientWidth;
        if (maxScroll <= 0) return;
        const maxStart = Math.max(0, n - w);
        const frac = maxStart <= 0 ? 0 : b.start / maxStart;
        host.scrollLeft = frac * maxScroll;
      } finally {
        queueMicrotask(() => {
          ignoreScrollSyncRef.current = false;
        });
      }
    };

    apply();
    const id = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(id);
  }, [data.length, brush.start, brush.end, innerTrackWidthPx, hostWidth]);

  const handleHorizontalScroll = useCallback(() => {
    if (ignoreScrollSyncRef.current) return;
    const host = viewportRef.current;
    if (!host) return;
    const n = dataLenRef.current;
    if (n <= 1) return;
    const b = brushRef.current;
    const span = b.end - b.start + 1;
    if (span >= n) return;

    const maxScroll = host.scrollWidth - host.clientWidth;
    if (maxScroll <= 0) return;
    const maxStart = Math.max(0, n - span);
    const frac = maxScroll <= 0 ? 0 : host.scrollLeft / maxScroll;
    const newStart = clampIndex(Math.round(frac * maxStart), 0, maxStart);
    if (newStart === b.start) return;
    setBrushManual({ start: newStart, end: newStart + span - 1 });
  }, []);

  const zoomInRange = useCallback(() => {
    const n = data.length;
    if (n <= 2) return;
    const { start: lo, end: hi } = brush;
    const w = hi - lo + 1;
    if (w <= 2) return;
    const newW = Math.max(2, Math.floor(w * 0.62));
    const mid = Math.floor((lo + hi) / 2);
    let ns = mid - Math.floor(newW / 2);
    let ne = ns + newW - 1;
    if (ns < 0) {
      ns = 0;
      ne = ns + newW - 1;
    }
    if (ne >= n) {
      ne = n - 1;
      ns = Math.max(0, ne - newW + 1);
    }
    setBrushManual({ start: ns, end: ne });
  }, [data.length, brush]);

  const zoomOutRange = useCallback(() => {
    const n = data.length;
    if (!n) return;
    const { start: lo, end: hi } = brush;
    const w = hi - lo + 1;
    const newW = Math.min(n, Math.max(w + 1, Math.ceil(w * 1.45)));
    const mid = Math.floor((lo + hi) / 2);
    let ns = mid - Math.floor(newW / 2);
    let ne = ns + newW - 1;
    if (ns < 0) {
      ns = 0;
      ne = Math.min(n - 1, newW - 1);
    }
    if (ne >= n) {
      ne = n - 1;
      ns = Math.max(0, ne - newW + 1);
    }
    if (newW >= n || (ns === 0 && ne === n - 1)) {
      setBrushManual(null);
      return;
    }
    setBrushManual({ start: clampIndex(ns, 0, n - 1), end: clampIndex(ne, 0, n - 1) });
  }, [data.length, brush]);

  const spansFullTimeline =
    data.length <= 1 || (brush.start === 0 && brush.end === data.length - 1);

  /** Tied to horizontal zoom: narrower X window ⇒ taller Y scale (max 2×). */
  const verticalScale = useMemo(() => {
    const n = data.length;
    if (n <= 1) return 1;
    const span = brush.end - brush.start + 1;
    const r = span / n;
    const raw = 1 + (1 - r) * 0.9;
    return Math.min(2, Math.max(1, raw));
  }, [data.length, brush.start, brush.end]);

  const yMargin = (plotHeight * (verticalScale - 1)) / 2;

  if (data.length <= 1) {
    return <>{children(data)}</>;
  }

  return (
    <div className="dynamics-zoom-wrap">
      <div className="dynamics-zoom-toolbar" aria-label="Chart zoom">
        <div className="dynamics-zoom-toolbar__buttons">
          <button
            type="button"
            className="dynamics-zoom-toolbar__btn"
            onClick={zoomInRange}
            disabled={!visibleData.length || brush.end - brush.start + 1 <= 2}
            title="Zoom in — narrower window; chart also grows vertically"
          >
            <ZoomIn size={14} aria-hidden /> Zoom in
          </button>
          <button
            type="button"
            className="dynamics-zoom-toolbar__btn"
            onClick={zoomOutRange}
            disabled={spansFullTimeline}
            title="Zoom out — wider window; chart height returns toward normal"
          >
            <ZoomOut size={14} aria-hidden /> Zoom out
          </button>
        </div>
      </div>
      <div className="dynamics-zoom-v-scroll">
        <div className="dynamics-zoom-v-scroll-inner">
          <div
            ref={viewportRef}
            className="dynamics-zoom-hscroll"
            onScroll={handleHorizontalScroll}
          >
            <div
              className="dynamics-zoom-hscroll-inner"
              style={{ width: innerTrackWidthPx, minWidth: innerTrackWidthPx }}
            >
              <div className="dynamics-zoom-y-stage">
                <div
                  className="dynamics-zoom-y-scale"
                  style={{
                    width: '100%',
                    paddingTop: yMargin,
                    paddingBottom: yMargin,
                    boxSizing: 'content-box',
                  }}
                >
                  <div
                    className="dynamics-zoom-y-scale-inner"
                    style={{
                      width: '100%',
                      height: plotHeight,
                      transform: `scaleY(${verticalScale})`,
                      transformOrigin: 'center center',
                    }}
                  >
                    {children(visibleData)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
